import { useEffect, useRef, useState } from "react";
import { App } from "antd";
import {
  useAcceptOdReviewIssueMutation,
  useGetOdReviewIssuesQuery,
  useMarkOdReviewIssueManualMutation,
  useRejectOdReviewIssueMutation,
  useResolveOdReviewIssueMutation,
} from "../../../store/api/odReviewApi";
import type { AxiosBaseQueryError } from "../../../store/api/axiosBaseQuery";
import type {
  OdReviewErrorType,
  OdReviewIssue,
} from "../../../services/odReview/odReview.types";
import {
  DEFAULT_HIDDEN_ERROR_TYPES,
  countIssuesByErrorType,
  filterIssuesByErrorType,
  toggleErrorTypeVisibility,
} from "./useOdReviewQueue.utils";

const isConflict = (error: unknown): boolean =>
  (error as AxiosBaseQueryError | undefined)?.status === 409;

export const isActionableIssue = (issue: OdReviewIssue): boolean =>
  issue.status === "PENDING" || issue.status === "PENDING_MANUAL";

export function useOdReviewQueue(runId: number, pollingIntervalMs?: number) {
  // The static `notification` from antd does not render under React 19; the
  // one from App.useApp() renders through the <App> in index.tsx.
  const { notification } = App.useApp();
  const { data: allIssues = [], isLoading, isFetching, refetch } =
    useGetOdReviewIssuesQuery(runId, {
      pollingInterval: pollingIntervalMs ?? 0,
    });
  const [hiddenErrorTypes, setHiddenErrorTypes] = useState<OdReviewErrorType[]>(
    DEFAULT_HIDDEN_ERROR_TYPES,
  );
  const issues = filterIssuesByErrorType(allIssues, hiddenErrorTypes);
  const errorTypeCounts = countIssuesByErrorType(allIssues);
  const hiddenIssueCount = allIssues.length - issues.length;
  const [cursorId, setCursorId] = useState<number | null>(null);
  const hasInitializedCursorRef = useRef(false);
  const [acceptOdReviewIssue] = useAcceptOdReviewIssueMutation();
  const [rejectOdReviewIssue] = useRejectOdReviewIssueMutation();
  const [markOdReviewIssueManual] = useMarkOdReviewIssueManualMutation();
  const [resolveOdReviewIssue] = useResolveOdReviewIssueMutation();

  useEffect(() => {
    if (hasInitializedCursorRef.current || issues.length === 0) return;
    const firstActionable = issues.find(isActionableIssue);
    setCursorId(firstActionable ? firstActionable.id : null);
    hasInitializedCursorRef.current = true;
  }, [issues]);

  useEffect(() => {
    if (cursorId === null) return;
    if (issues.some((issue: OdReviewIssue) => issue.id === cursorId)) return;
    const firstActionable = issues.find(isActionableIssue);
    setCursorId(firstActionable ? firstActionable.id : null);
  }, [issues, cursorId]);

  const currentIndex = issues.findIndex((i: OdReviewIssue) => i.id === cursorId);
  const currentIssue = currentIndex >= 0 ? issues[currentIndex] : null;
  const total = issues.length;
  const queueComplete = total > 0 && !issues.some(isActionableIssue);

  const goToNext = () => {
    if (currentIndex < 0 || currentIndex >= issues.length - 1) return;
    setCursorId(issues[currentIndex + 1].id);
  };

  const goToPrev = () => {
    if (currentIndex <= 0) return;
    setCursorId(issues[currentIndex - 1].id);
  };

  const selectIssue = (issue: OdReviewIssue) => {
    setCursorId(issue.id);
  };

  const advanceToNextActionable = () => {
    const startIndex = currentIndex < 0 ? 0 : currentIndex + 1;
    const next = issues.slice(startIndex).find(isActionableIssue);
    setCursorId(next ? next.id : null);
  };

  const accept = async (appliedValue: string): Promise<boolean> => {
    if (!currentIssue) return false;
    try {
      await acceptOdReviewIssue({
        id: currentIssue.id,
        runId,
        applied_value: appliedValue,
      }).unwrap();
      advanceToNextActionable();
      return true;
    } catch (error) {
      if (isConflict(error)) {
        notification.error({
          message: "Contenido modificado",
          description:
            "El contenido cambió desde que se detectó este error. Quedó como Pendiente manual.",
          placement: "top",
        });
        advanceToNextActionable();
        refetch();
        return false;
      }
      notification.error({
        message: "Error",
        description: "No se pudo aceptar el cambio.",
        placement: "top",
      });
      return false;
    }
  };

  const reject = async () => {
    if (!currentIssue) return;
    try {
      await rejectOdReviewIssue({ id: currentIssue.id, runId }).unwrap();
      advanceToNextActionable();
    } catch {
      notification.error({
        message: "Error",
        description: "No se pudo rechazar el cambio.",
        placement: "top",
      });
    }
  };

  const markManual = async () => {
    if (!currentIssue) return;
    try {
      await markOdReviewIssueManual({ id: currentIssue.id, runId }).unwrap();
      advanceToNextActionable();
    } catch {
      notification.error({
        message: "Error",
        description: "No se pudo marcar el issue para reemplazo.",
        placement: "top",
      });
    }
  };

  const resolveManual = async () => {
    if (!currentIssue) return;
    try {
      await resolveOdReviewIssue({ id: currentIssue.id, runId }).unwrap();
      advanceToNextActionable();
    } catch {
      notification.error({
        message: "Error",
        description: "No se pudo marcar el issue como resuelto a mano.",
        placement: "top",
      });
    }
  };

  const toggleErrorType = (errorType: OdReviewErrorType) => {
    setHiddenErrorTypes((hidden) =>
      toggleErrorTypeVisibility(hidden, errorType),
    );
  };

  return {
    issues,
    errorTypeCounts,
    hiddenErrorTypes,
    hiddenIssueCount,
    toggleErrorType,
    isLoading: isLoading || isFetching,
    currentIssue,
    currentIndex,
    total,
    queueComplete,
    hasNext: currentIndex >= 0 && currentIndex < issues.length - 1,
    hasPrev: currentIndex > 0,
    goToNext,
    goToPrev,
    selectIssue,
    accept,
    reject,
    markManual,
    resolveManual,
  };
}
